import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  CalendarClock,
  ClipboardCheck,
  Command,
  FileSignature,
  Loader2,
  Plus,
  Search,
  Settings,
  UsersRound,
  X
} from 'lucide-react';
import { jobAPI, candidateAPI } from '../../services/api';

// ─── Static quick-nav items ─────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'nav-jobs',         label: 'Jobs',              sub: 'Browse all roles',            icon: Briefcase,     path: '/jobs' },
  { id: 'nav-jobs-create',  label: 'Create a new job',  sub: 'Post a new role',             icon: Plus,          path: '/jobs/create' },
  { id: 'nav-candidates',   label: 'Candidates',        sub: 'Review your match pipeline',  icon: UsersRound,    path: '/candidates' },
  { id: 'nav-interviews',   label: 'Interviews',        sub: 'Scheduled & pending',         icon: CalendarClock, path: '/interviews' },
  { id: 'nav-offers',       label: 'Offers',            sub: 'Sent & accepted offers',      icon: FileSignature, path: '/offers' },
  { id: 'nav-assessments',  label: 'Assessments',       sub: 'Manage evaluations',          icon: ClipboardCheck,path: '/assessments' },
  { id: 'nav-reports',      label: 'Reports',           sub: 'Analytics & exports',         icon: BarChart3,     path: '/reports' },
  { id: 'nav-config',       label: 'Configuration',     sub: 'Matching & scoring settings', icon: Settings,      path: '/config' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const labelize = (value) =>
  String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const highlight = (text, query) => {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-sky-100 text-sky-800 not-italic">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const candidateName = (match) => {
  const c = match?.candidate || {};
  return c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Candidate';
};

// ─── Result row component ────────────────────────────────────────────────────
const ResultRow = ({ icon: Icon, label, sub, tone = 'slate', active, onClick, query }) => (
  <button
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
      active ? 'bg-[#0b1f3a] text-white shadow-sm' : 'hover:bg-slate-50'
    }`}
  >
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
      active
        ? 'bg-white/15 text-white'
        : tone === 'sky'   ? 'bg-sky-50 text-sky-600'
        : tone === 'green' ? 'bg-emerald-50 text-emerald-600'
        : 'bg-slate-100 text-slate-500'
    }`}>
      <Icon size={15} />
    </span>
    <span className="min-w-0 flex-1">
      <span className={`block truncate text-sm font-medium ${active ? 'text-white' : 'text-[#0b1f3a]'}`}>
        {typeof label === 'string' ? highlight(label, query) : label}
      </span>
      {sub && (
        <span className={`block truncate text-xs ${active ? 'text-white/70' : 'text-slate-400'}`}>
          {sub}
        </span>
      )}
    </span>
    <ArrowRight size={14} className={`shrink-0 opacity-0 transition-opacity ${active ? 'opacity-70' : 'group-hover:opacity-40'}`} />
  </button>
);

// ─── Section label ───────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div className="mb-1 mt-4 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 first:mt-0">
    {children}
  </div>
);

// ─── Main CommandPalette component ────────────────────────────────────────────
const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 220);
    return () => clearTimeout(timer);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
      setQuery('');
      setDebouncedQuery('');
      setJobs([]);
      setCandidates([]);
      setActiveIndex(0);
    }
  }, [open]);

  // Fetch search results when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      setJobs([]);
      setCandidates([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [jobsRes, candidatesRes] = await Promise.allSettled([
          jobAPI.getEmployerJobs({ limit: 6, search: debouncedQuery, sortBy: 'updated_at', sortOrder: 'DESC' }),
          candidateAPI.getEmployerCandidates({ limit: 6, search: debouncedQuery, minScore: 0 })
        ]);
        if (cancelled) return;
        const rawJobs = jobsRes.status === 'fulfilled' ? (jobsRes.value?.jobs || jobsRes.value?.data || []) : [];
        const rawCandidates = candidatesRes.status === 'fulfilled'
          ? (candidatesRes.value?.candidates || candidatesRes.value?.data || [])
          : [];
        setJobs(rawJobs.slice(0, 5));
        setCandidates(rawCandidates.slice(0, 5));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Build flat list of all visible items for keyboard nav
  const allItems = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const filteredNav = trimmed
      ? NAV_ITEMS.filter(
          (item) =>
            item.label.toLowerCase().includes(trimmed) ||
            item.sub.toLowerCase().includes(trimmed)
        )
      : NAV_ITEMS;

    return [
      ...filteredNav.map((item) => ({ ...item, type: 'nav' })),
      ...jobs.map((job) => ({
        id: `job-${job.id}`,
        type: 'job',
        label: job.title || 'Untitled job',
        sub: `${labelize(job.status)} · ${labelize(job.jobType)} · ${job.location || 'No location'}`,
        icon: Briefcase,
        tone: 'sky',
        path: `/jobs/${job.id}`
      })),
      ...candidates.map((match) => ({
        id: `candidate-${match.candidateId || match.matchId}`,
        type: 'candidate',
        label: candidateName(match),
        sub: `${labelize(match.matchBand ? `Band ${match.matchBand}` : '')} ${Math.round(Number(match.overallMatchScore || 0))}% · ${match.job?.title || 'No role'}`,
        icon: UsersRound,
        tone: 'green',
        path: `/candidates/${match.candidateId || match.candidate?.id || match.matchId}`
      }))
    ];
  }, [query, jobs, candidates]);

  // Clamp active index whenever list changes
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(allItems.length - 1, 0)));
  }, [allItems]);

  const go = useCallback((path) => {
    navigate(path);
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allItems[activeIndex];
      if (item?.path) go(item.path);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [allItems, activeIndex, go, onClose]);

  // Scroll active item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Reset active index when query changes
  useEffect(() => { setActiveIndex(0); }, [query]);

  if (!open) return null;

  // Determine sections to show
  const trimmed = query.trim().toLowerCase();
  const navSection = allItems.filter((i) => i.type === 'nav');
  const jobSection = allItems.filter((i) => i.type === 'job');
  const candidateSection = allItems.filter((i) => i.type === 'candidate');

  // Map items to global indices for active highlighting
  let globalIdx = 0;
  const withIndex = (items) =>
    items.map((item) => ({ ...item, globalIdx: globalIdx++ }));

  const navWithIdx = withIndex(navSection);
  const jobWithIdx = withIndex(jobSection);
  const candidateWithIdx = withIndex(candidateSection);

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-start justify-center px-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0b1f3a]/40 backdrop-blur-sm" aria-hidden="true" />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_80px_rgba(11,31,58,0.22)]">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5">
          {loading
            ? <Loader2 size={18} className="shrink-0 animate-spin text-sky-500" />
            : <Search size={18} className="shrink-0 text-slate-400" />
          }
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search jobs, candidates, or navigate…"
            className="min-w-0 flex-1 bg-transparent text-sm text-[#0b1f3a] outline-none placeholder:text-slate-400"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            onClick={onClose}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition hover:bg-slate-200"
            aria-label="Close palette"
          >
            <X size={13} />
          </button>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto overscroll-contain p-2">
          {/* Navigation section */}
          {navWithIdx.length > 0 && (
            <>
              <SectionLabel>{trimmed ? 'Pages' : 'Navigate to'}</SectionLabel>
              {navWithIdx.map((item) => (
                <div key={item.id} data-active={item.globalIdx === activeIndex}>
                  <ResultRow
                    icon={item.icon}
                    label={item.label}
                    sub={item.sub}
                    active={item.globalIdx === activeIndex}
                    onClick={() => go(item.path)}
                    query={trimmed}
                  />
                </div>
              ))}
            </>
          )}

          {/* Jobs section */}
          {jobWithIdx.length > 0 && (
            <>
              <SectionLabel>Jobs</SectionLabel>
              {jobWithIdx.map((item) => (
                <div key={item.id} data-active={item.globalIdx === activeIndex}>
                  <ResultRow
                    icon={item.icon}
                    label={item.label}
                    sub={item.sub}
                    tone="sky"
                    active={item.globalIdx === activeIndex}
                    onClick={() => go(item.path)}
                    query={trimmed}
                  />
                </div>
              ))}
            </>
          )}

          {/* Candidates section */}
          {candidateWithIdx.length > 0 && (
            <>
              <SectionLabel>Candidates</SectionLabel>
              {candidateWithIdx.map((item) => (
                <div key={item.id} data-active={item.globalIdx === activeIndex}>
                  <ResultRow
                    icon={item.icon}
                    label={item.label}
                    sub={item.sub}
                    tone="green"
                    active={item.globalIdx === activeIndex}
                    onClick={() => go(item.path)}
                    query={trimmed}
                  />
                </div>
              ))}
            </>
          )}

          {/* Empty state when searching with no results */}
          {trimmed && !loading && allItems.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Search size={24} className="text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-xs text-slate-400">Try a job title, candidate name, or page name</p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5">
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">↵</kbd>
              open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd>
              close
            </span>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-slate-400">
            <Command size={11} />
            <span>K</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
